import {
	Badge,
	Box,
	Button,
	Inline,
	Heading,
	Text,
	Icons,
	Stack,
	Container,
	joinAttributes,
	Spinner,
	Component,
	Money,
	Table,
	Divider,
	Time,
	Alert,
	TableFooterDataCell,
	Grid,
	Col,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	queryToSearch,
	useGetBackUrlFromLocation,
	Breadcrumbs,
	Link,
} from "@sembark-travel/ui-router"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	dateToQuery,
	getDiff,
	getDiffBetweenLocalAndUtcTimestamp,
	localOrUtcTimestampToLocalDate,
	formatDate,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react"
import { Email, PhoneNumber } from "../Contacts"
import { generatePath, joinPaths } from "../router-utils"
import {
	QR_ERRORS,
	TActivityVoucher,
	TRegenrateActivityVoucher,
	TUploadMethods,
	type TTravelActiviytBooking,
} from "./store"
import useSWR from "swr"
import { useXHR } from "@sembark-travel/xhr"
import { ITrip } from "../Trips"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { EditTravelActivityBookingItemInDialog } from "./EditItem"
import { GuestDetails, SelectTourist, TTourist } from "../Tourists"
import { IBooking, TSelfBookedHotels } from "../HotelBookings"
import { HotelBookings, combineBookings } from "../TripOperationalBookings"
import {
	arrayMutators,
	CheckboxInputField,
	DatePickerField,
	FieldArray,
	FileInputField,
	Form,
	GetFieldValue,
	MarkdownInputField,
	RadioInputField,
	SelectField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	useFieldValue,
	useForm,
	withServerErrors,
	addKeyToFieldArrayItem,
	getFieldArrayItemKey,
	validateFormValues,
} from "@sembark-travel/ui-form"
import config from "../config"
import { Html5Qrcode, Html5QrcodeScanner } from "html5-qrcode"
import { RenderPickUpDropLocation } from "../CabScheduling"
import * as Validator from "yup"

function useBookingDetails(bookingId: number | string) {
	const xhr = useXHR()
	const { data, mutate } = useSWR(
		`/travel-activity-bookings/${bookingId}`,
		() =>
			xhr
				.get<{
					data: TTravelActiviytBooking & {
						trip: ITrip
					}
					meta: {
						checkin_hotels: Array<IBooking>
						checkout_hotels: Array<IBooking>
						stay_hotels: Array<IBooking>
						self_booked_checkin_hotels: Array<TSelfBookedHotels>
						self_booked_checkout_hotels: Array<TSelfBookedHotels>
						self_booked_stay_hotels: Array<TSelfBookedHotels>
					}
				}>(`/travel-activity-bookings/${bookingId}`)
				.then((resp) => resp.data)
	)
	return { data, mutate }
}

function useDeleteBooking(bookingId: number | string) {
	const xhr = useXHR()
	return async function () {
		return xhr
			.delete<{ message: string }>(`/travel-activity-bookings/${bookingId}`)
			.then((resp) => resp.data)
	}
}

export function TravelActivityBookingItem({
	bookingId: scheduleId,
}: {
	bookingId: string
}) {
	const { data, mutate: revalidate } = useBookingDetails(scheduleId)
	const backUrl =
		useGetBackUrlFromLocation() ||
		generatePath("/cab-schedules") +
			queryToSearch({
				date: data
					? dateToQuery(
							localOrUtcTimestampToLocalDate(
								data.data.start_date_local,
								data.data.start_date
							)
						)
					: undefined,
			})
	if (!data) return <Spinner padding="4" alignCenter />
	const { id, activity } = data.data
	return (
		<Box key={id}>
			<Breadcrumbs
				title="Travel Activity Booking Details"
				items={[
					[backUrl, "Travel Activity Bookings"],
					["", activity.name],
				]}
			/>
			<Container paddingY="6" bgColor="default">
				<TravelActivityBookingDetails
					booking={data.data}
					onRefresh={revalidate}
				/>
			</Container>
		</Box>
	)
}

export function TravelActivityBookingDetails({
	booking: propBooking,
	disableEdit,
	onRefresh,
	actions = null,
	onDeletion,
	onShare,
}: {
	booking: TTravelActiviytBooking
	disableEdit?: boolean
	onRefresh?: () => void
	actions?: React.ReactNode
	onDeletion?: () => void
	onShare?: () => void
}) {
	const { data, mutate } = useBookingDetails(propBooking.id)
	const revalidate = useCallback(() => {
		mutate()
		onRefresh?.()
	}, [mutate, onRefresh])
	if (!data) return <Spinner padding="4" alignCenter />
	const {
		data: booking,
		meta: {
			checkin_hotels,
			checkout_hotels,
			stay_hotels,
			self_booked_checkin_hotels,
			self_booked_checkout_hotels,
			self_booked_stay_hotels,
		},
	} = data
	const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
		checkout_hotels,
		checkin_hotels,
		stay_hotels,
		self_booked_checkout_hotels,
		self_booked_checkin_hotels,
		self_booked_stay_hotels,
		showSelfBooked: true,
	})
	const {
		start_date,
		start_date_local,
		activity,
		ticket_type,
		ticket_tourist_configurations,
		trip,
		itinerary,
		supplier,
		supplier_contact,
		confirmation_details,
		remarks,
		comments,
		is_booked,
		confirmed_at,
		confirmed_by,
		currency,
		booked_price,
		duration_formatted,
		start_time_formatted,
		end_time_formatted,
		can_modify,
		tourists,
		pick_up_location,
		drop_location,
		use_hotel_as_pickup,
		use_hotel_as_drop,
	} = booking
	disableEdit = disableEdit || !can_modify
	const timezoneDiff = getDiffBetweenLocalAndUtcTimestamp(
		trip.start_date_local,
		trip.start_date
	)
	const isComplementary = ticket_tourist_configurations.some(
		({ complementary }) => complementary > 0
	)
	return (
		<Stack gap="6">
			{timezoneDiff ? (
				<Alert status="warning" title="Trip details in Different Timezone">
					The Timezone of this trip differs from your local timezone by{" "}
					{timezoneDiff} mins. Please avoid modifying details of this trip.
				</Alert>
			) : null}
			<Inline
				collapseBelow="md"
				gap="6"
				justifyContent="between"
				flexWrap="wrap"
			>
				<Stack gap="2" flex="1">
					<Stack gap="px">
						<Heading as="h3">
							{activity.name}
							{ticket_type ? ` - ${ticket_type.display_name}` : null}
						</Heading>
						{ticket_type?.short_name ? (
							<Text color="muted" fontSize="sm">
								[[{ticket_type.short_name}]]
							</Text>
						) : null}
						{itinerary?.description ? (
							<Component initialState={false}>
								{({ state, setState }) => (
									<Stack gap="1">
										<Box>
											<Button
												title={itinerary.name}
												inline
												onClick={() => setState(!state)}
											>
												<Text
													as="span"
													fontWeight="semibold"
													color="primary"
													fontSize="sm"
												>
													Itinerary
												</Text>
												<Text as="span">
													<Icons.ChevronDown
														rotate={state ? "180" : "270"}
														transition="1"
													/>
												</Text>
											</Button>
										</Box>
										{state ? (
											<Stack
												color="muted"
												fontSize="sm"
												gap="1"
												backgroundColor="subtle"
												borderWidth="1"
												padding="2"
												rounded="md"
											>
												<Markdown>{itinerary.description}</Markdown>
											</Stack>
										) : null}
									</Stack>
								)}
							</Component>
						) : null}
					</Stack>
					<Inline gap="2">
						<Box>
							<Icons.Calendar color="muted" size="6" />
						</Box>
						<Stack gap="1">
							<Inline gap="2">
								<Box fontSize="md" fontWeight="semibold">
									{formatDate(
										localOrUtcTimestampToLocalDate(
											start_date_local,
											start_date
										),
										"ddd Do MMM, YYYY"
									)}
								</Box>
								<Box>
									<Badge primary outlined>
										{withOrdinalSuffix(
											getDiff(
												localOrUtcTimestampToLocalDate(
													start_date_local,
													start_date
												),
												localOrUtcTimestampToLocalDate(
													trip.start_date_local,
													trip.start_date
												),
												"days"
											) + 1
										)}{" "}
										Day
									</Badge>
								</Box>
							</Inline>
							{start_time_formatted || duration_formatted ? (
								<Text fontSize="sm">
									{joinAttributes(
										start_time_formatted
											? `Starts: ${start_time_formatted}`
											: null,
										duration_formatted
											? `Duration: ${duration_formatted}${end_time_formatted ? ` (Ends: ${end_time_formatted})` : ``}`
											: null
									)}
								</Text>
							) : null}
							<RenderPickUpDropLocation
								checkinHotels={checkinHotels}
								checkoutHotels={checkoutHotels}
								stayHotels={stayHotels}
								useHotelAsPickup={use_hotel_as_pickup}
								useHotelAsDrop={use_hotel_as_drop}
								pickupLocation={pick_up_location}
								dropLocation={drop_location}
							/>
						</Stack>
					</Inline>
				</Stack>
				<Inline gap="4" flex="1" flexWrap="wrap">
					<Inline gap="2" flex="1">
						<Box>
							<Icons.User color="muted" size="6" />
						</Box>
						<Stack gap="1">
							<Inline flexWrap="wrap" gap="2" alignItems="center">
								{trip.tourist ? (
									<Text fontWeight="semibold" fontSize="md">
										{trip.tourist.name}
									</Text>
								) : (
									<Text color="warning" fontSize="sm">
										<Icons.Attention /> Guest details missing!
									</Text>
								)}
								<Box color="muted" fontSize="sm">
									{joinAttributes(
										trip.tourist?.phone_numbers?.length ? (
											<PhoneNumber value={trip.contact.phone_numbers} />
										) : null,
										trip.tourist?.email ? (
											<Email value={trip.contact.email} iconOnly />
										) : null,
										`${trip.no_of_adults}A${
											trip.children.length ? `, ${trip.children.length}C` : ``
										}`
									)}
								</Box>
							</Inline>
							<Box color="muted">
								{joinAttributes(
									<Link
										to={joinPaths(
											generatePath("/trips/:tripId/services-bookings", {
												tripId: trip.id.toString(),
											}) + `/cabs`
										)}
										color="accent"
									>
										Trip {trip.id}
									</Link>,
									trip.trip_source.short_name,
									trip.trip_source_contact ? (
										<PhoneNumber
											value={trip.trip_source_contact.phone_numbers}
											iconOnly
										/>
									) : null
								)}
							</Box>
							{trip.due_payments_count ===
							undefined ? null : trip.due_payments_count > 0 ? (
								<Box>
									<Badge warning>Payments Due</Badge>
								</Box>
							) : (
								<Box>
									<Badge success title="Payments Received">
										Payments Clear
									</Badge>
								</Box>
							)}
						</Stack>
					</Inline>
					<Stack gap="1" flex="1">
						<Text fontSize="sm" color="muted" fontWeight="semibold">
							Operations Team
						</Text>
						{trip.operations_team?.length ? (
							<Stack gap="1">
								{trip.operations_team.map((t) => (
									<Text key={t.id}>{t.name}</Text>
								))}
							</Stack>
						) : (
							<Text fontSize="xs">N/A</Text>
						)}
					</Stack>
				</Inline>
			</Inline>
			<Inline flexWrap="wrap" collapseBelow="sm" gap="4">
				<Heading fontSize="md">Ticket Configurations</Heading>
				<Box flex="1">
					<Table
						headers={[
							"Type",
							"Qty.",
							...(isComplementary ? [<Box title="Free of Cost">FOC</Box>] : []),
							"Rate",
							"Total",
						]}
						alignCols={
							isComplementary
								? { 1: "center", 2: "center", 3: "right", 4: "right" }
								: { 1: "center", 2: "right", 3: "right" }
						}
						bordered
						responsive
						rows={ticket_tourist_configurations.map(
							({
								configuration,
								quantity,
								currency,
								complementary,
								per_quantity_booked_price,
								booked_price,
							}) => [
								configuration.name,
								quantity,
								...(isComplementary ? [complementary || 0] : []),
								<Money
									currency={currency}
									amount={per_quantity_booked_price || 0}
								/>,
								<Money
									currency={currency}
									amount={booked_price || 0}
									showCurrency
								/>,
							]
						)}
					>
						<tfoot>
							<tr>
								<TableFooterDataCell
									colSpan={isComplementary ? 4 : 3}
									textAlign={"right"}
									fontWeight="semibold"
								>
									Total:
								</TableFooterDataCell>
								<TableFooterDataCell textAlign="right" fontWeight="semibold">
									<Money
										currency={currency}
										amount={booked_price}
										showCurrency
									/>
								</TableFooterDataCell>
							</tr>
						</tfoot>
					</Table>
				</Box>
			</Inline>
			<Divider marginY="0" />
			<Inline gap="6" flexWrap="wrap" collapseBelow="sm">
				<Stack gap="1" flex="1">
					<Text color="muted" fontSize="sm" fontWeight="semibold">
						Booking Status
					</Text>
					{is_booked ? (
						<Stack gap="2">
							<Box>
								<Badge success>Booked</Badge>
							</Box>
							{confirmed_by || confirmed_at ? (
								<Stack gap="px">
									{confirmed_by ? (
										<Text fontSize="sm">{confirmed_by.name}</Text>
									) : null}
									{confirmed_at ? (
										<Text>
											<Time
												fontSize="sm"
												color="muted"
												timestamp={confirmed_at}
											/>
										</Text>
									) : null}
								</Stack>
							) : null}
						</Stack>
					) : (
						<Box>
							<Badge>Pending</Badge>
						</Box>
					)}
				</Stack>
				{supplier ? (
					<Stack gap="1" flex="1">
						<Text color="muted" fontSize="sm" fontWeight="semibold">
							Supplier Details
						</Text>
						<Box fontWeight="semibold">
							<Link
								color="accent"
								fontSize="md"
								to={generatePath("/suppliers/:supplierId", {
									supplierId: supplier.id.toString(),
								})}
							>
								{supplier.name}
							</Link>
						</Box>
						<Box>
							{supplier.contacts?.length
								? joinAttributes(
										supplier.contacts[0].phone_numbers?.length ? (
											<PhoneNumber value={supplier.contacts[0].phone_numbers} />
										) : null,
										supplier.contacts[0].email ? (
											<Email value={supplier.contacts[0].email} iconOnly />
										) : null
									)
								: null}
						</Box>
					</Stack>
				) : null}
				{supplier_contact ? (
					<Stack gap="1" flex="1">
						<Text color="muted" fontSize="sm" fontWeight="semibold">
							Supplier Contact
						</Text>
						<Box fontWeight="semibold">
							{supplier_contact.name}&nbsp;
							{supplier_contact.deleted_at ? (
								<Icons.Ban
									color="warning"
									title={`Disabled on ${utcTimestampToLocalDateTimeString(supplier_contact.deleted_at)}`}
								/>
							) : null}
						</Box>
						{supplier_contact.phone_numbers?.length ||
						supplier_contact.email ? (
							<Box fontSize="sm">
								{joinAttributes(
									supplier_contact.phone_numbers?.length ? (
										<PhoneNumber value={supplier_contact.phone_numbers} />
									) : null,
									supplier_contact.email ? (
										<Email value={supplier_contact.email} iconOnly />
									) : null
								)}
							</Box>
						) : null}
					</Stack>
				) : null}
			</Inline>
			<GuestDetails tourists={tourists} />
			<Divider marginY="0" />
			<Inline gap="6" collapseBelow="md">
				<Stack gap="1" flex="1">
					<Text color="muted" fontSize="sm" fontWeight="semibold">
						Confirmation Details
					</Text>
					{confirmation_details ? (
						<Box
							whiteSpace="preserveAndWrap"
							borderWidth="1"
							bgColor="subtle"
							padding="2"
							rounded="md"
						>
							{confirmation_details}
						</Box>
					) : (
						<Text color="muted">N/A</Text>
					)}
				</Stack>
				<Stack gap="1" flex="1">
					<Text color="muted" fontSize="sm" fontWeight="semibold">
						Remarks for Supplier and Customer
					</Text>
					{remarks ? (
						<Box
							whiteSpace="preserveAndWrap"
							borderWidth="1"
							bgColor="subtle"
							padding="2"
							rounded="md"
						>
							{remarks}
						</Box>
					) : (
						<Text color="muted">N/A</Text>
					)}
				</Stack>
				<Stack gap="1" flex="1">
					<Text color="muted" fontSize="sm" fontWeight="semibold">
						Internal Comments
					</Text>
					{comments ? (
						<Box
							whiteSpace="preserveAndWrap"
							borderWidth="1"
							bgColor="subtle"
							padding="2"
							rounded="md"
						>
							{comments}
						</Box>
					) : (
						<Text color="muted">N/A</Text>
					)}
				</Stack>
			</Inline>
			{checkinHotels.length || checkoutHotels.length || stayHotels.length ? (
				<>
					<Divider marginY="0" />
					<Stack gap="2">
						<Heading as="h4" fontSize="base" color="primary">
							Hotels
						</Heading>
						<Inline flexWrap="wrap" gap="4">
							{checkoutHotels?.length ? (
								<Box>
									<HotelBookings
										bookings={checkoutHotels}
										label="Check-Out"
										icon="Logout"
									/>
								</Box>
							) : null}
							{checkinHotels?.length ? (
								<Box>
									<HotelBookings
										bookings={checkinHotels}
										label="Check-In"
										icon="Login"
									/>
								</Box>
							) : null}
							{stayHotels?.length ? (
								<Box>
									<HotelBookings
										bookings={stayHotels}
										label="Stay"
										icon="Bed"
									/>
								</Box>
							) : null}
						</Inline>
					</Stack>
				</>
			) : null}
			<Divider marginY="0" />
			<Inline gap="4" justifyContent="between" flexWrap="wrap">
				<Inline gap="4">
					{!disableEdit && !trip.is_locked ? (
						<EditTravelActivityBookingItemInDialog
							bookingId={booking.id}
							onSuccess={() => revalidate()}
						>
							{({ onEdit }) => (
								<Button onClick={() => onEdit()}>
									<Icons.Pencil /> Edit
								</Button>
							)}
						</EditTravelActivityBookingItemInDialog>
					) : null}
					{onShare ? (
						<Button onClick={() => onShare()}>
							<Icons.Share /> Share
						</Button>
					) : null}
				</Inline>
				{onDeletion && !disableEdit && !is_booked && !trip.is_locked ? (
					<Box>
						<DeleteTravelActivityBookingInDialog
							id={propBooking.id}
							onSuccess={onDeletion}
						>
							{({ onDelete, isDeleting }) => (
								<Button
									status="warning"
									disabled={isDeleting}
									onClick={() => {
										onDelete()
									}}
								>
									{isDeleting ? (
										"Deleting..."
									) : (
										<>
											<Icons.Trash /> Delete
										</>
									)}
								</Button>
							)}
						</DeleteTravelActivityBookingInDialog>
					</Box>
				) : null}
				{actions}
			</Inline>
		</Stack>
	)
}

export function DeleteTravelActivityBookingInDialog({
	id,
	onSuccess,
	children,
}: {
	id: number
	onSuccess?: () => void
	children: (props: {
		onDelete: () => void
		isDeleting: boolean
	}) => React.ReactNode
}) {
	const deleteSchedule = useDeleteBooking(id)
	const [isDeleting, setIsDeleting] = useState(false)
	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					{children({
						isDeleting,
						onDelete: async () => {
							if (isDeleting) return
							if (await confirm()) {
								setIsDeleting(true)
								deleteSchedule()
									.then(({ message }) => {
										showSnackbar(message)
										onSuccess?.()
									})
									.catch((error) => {
										alert(error.message || "Something went wrong")
									})
									.then(() => {
										setIsDeleting(false)
									})
							}
						},
					})}
					<ConfirmationDialog
						title="Confirmation"
						destructiveAction
						acceptActionLabel="I understand, Delete It"
					>
						<Stack gap="2">
							<Text fontWeight="semibold" color="warning">
								<Icons.Attention /> This action can not be reversed.
							</Text>
							<Text>
								Are you sure you want to delete this Travel Activity Booking ?
							</Text>
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

export function TravelActivityBookingDetailsInDialog({
	children,
	...props
}: React.ComponentProps<typeof TravelActivityBookingDetails> & {
	children: (props: { onShow: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onShow: open })}
			{isOpen ? (
				<Dialog open onClose={close} title="Travel Activity Booking Details" lg>
					<Dialog.Body paddingX="4">
						<TravelActivityBookingDetails
							{...props}
							onDeletion={() => {
								close()
								props.onRefresh?.()
							}}
						/>
					</Dialog.Body>
				</Dialog>
			) : null}
		</>
	)
}

export function ActivityVoucherActions({
	totalPax,
	booking,
	onRefresh,
}: {
	totalPax: number
	booking: TTravelActiviytBooking
	onRefresh?: () => void
}) {
	const { is_booked, voucher } = booking
	const [batch, setBatch] = useState<TBatch | undefined>(undefined)
	const handleSuccess = (batch?: TBatch) => {
		onRefresh?.()
		if (batch) {
			setBatch(batch)
		}
	}

	// Early return if voucher needs to be generated
	if (!voucher && is_booked) {
		return (
			<GenerateActivityVoucher
				booking={booking}
				onSuccess={handleSuccess}
				totalPax={totalPax}
			/>
		)
	}
	return (
		<Inline marginTop="2" alignItems="center">
			{joinAttributes(
				<DownloadVoucher
					totalPax={totalPax}
					batch={batch}
					voucher={voucher}
					onSuccess={handleSuccess}
					booking={booking}
				/>,
				<VoucherStatus voucher={voucher} onSuccess={handleSuccess} />,
				<GenerateVoucherForm
					totalPax={totalPax}
					booking={booking}
					onSuccess={handleSuccess}
					regenrate={true}
					voucherId={voucher?.id}
				>
					<Icons.Refresh opacity="50" />
				</GenerateVoucherForm>
			)}
		</Inline>
	)
}

// Subcomponent for showing Voucher Sent / Pending
function VoucherStatus({
	voucher,
	onSuccess,
}: {
	voucher?: TActivityVoucher
	onSuccess: (batch?: TBatch) => void
}) {
	const isVoucherSent = voucher && Boolean(voucher?.sent_at && voucher?.sent_by)

	if (isVoucherSent) {
		return (
			<Text
				fontSize="sm"
				as="span"
				title={`Voucher sent on ${voucher?.sent_at ? utcTimestampToLocalDateTimeString(voucher?.sent_at) : null} by ${voucher.sent_by?.name}`}
			>
				<Icons.Ok /> Voucher Sent
			</Text>
		)
	}

	return (
		<MarkVoucherAsSent voucherId={voucher?.id} onSuccess={onSuccess} minimal>
			<Text as="span" color="warning">
				<Icons.AttentionSolid /> Voucher Pending
			</Text>
		</MarkVoucherAsSent>
	)
}

export function MarkVoucherAsSent({
	voucherId,
	onSuccess,
	confirmationMessage = "You will be marked as the Voucher sender for this booking.",
	children,
	minimal,
}: {
	voucherId?: number
	onSuccess?: () => void
	confirmationMessage?: string
	children?: React.ReactNode
	minimal?: boolean
}) {
	const xhr = useXHR()
	const [sending, setSending] = useState(false)
	if (!voucherId) return <>{children}</>
	return (
		<Button
			onClick={async () => {
				if (globalThis.confirm(confirmationMessage)) {
					setSending(true)
					try {
						await xhr.post(`/send-travel-activity-booking-voucher/${voucherId}`)
						onSuccess?.()
					} catch (err) {
						const e = err as Error
						showSnackbar(
							e?.message || "Could not mark as sent. Please try again later."
						)
					} finally {
						setSending(false)
					}
				}
			}}
			title="Mark voucher as Sent"
			disabled={sending}
			{...(minimal
				? {
						inline: true,
						size: "sm",
					}
				: {})}
		>
			{sending ? "Sending..." : children || "Mark as Sent"}
		</Button>
	)
}

type TActivityVoucherForm = Omit<TActivityVoucher, "voucher_activities"> & {
	voucher_activities?: Array<
		NonNullable<TActivityVoucher["voucher_activities"]>[number] & {
			__id?: number
			tickets: Array<
				NonNullable<
					TActivityVoucher["voucher_activities"]
				>[number]["tickets"][number] & {
					__id?: number
					tourists?: Array<
						NonNullable<
							NonNullable<
								TActivityVoucher["voucher_activities"]
							>[number]["tickets"][number]["tourists"]
						>[number] & { __id?: number }
					>
				}
			>
		}
	>
}

export function GenerateActivityVoucher({
	booking,
	totalPax,
	onSuccess,
}: {
	totalPax: number
	booking: TTravelActiviytBooking
	onSuccess: (batch?: TBatch) => void
}) {
	if (!booking.is_booked) return null
	return (
		<GenerateVoucherForm
			booking={booking}
			onSuccess={onSuccess}
			totalPax={totalPax}
		>
			<Text as="span" color="warning">
				<Icons.Document /> Generate Voucher
			</Text>
		</GenerateVoucherForm>
	)
}

function formatFileSize(size: number) {
	const KB = 1024
	const MB = KB * 1024
	if (size < KB) {
		return `${size} B`
	} else if (size < MB) {
		return `${(size / KB).toFixed(1)} KB`
	} else {
		return `${(size / MB).toFixed(2)} MB`
	}
}

function checkDeletedTourist(values: TActivityVoucherForm) {
	if (Array.isArray(values.voucher_activities)) {
		for (const activity of values.voucher_activities) {
			const activityName = activity.title
			for (const ticket of activity.tickets ?? []) {
				for (const t of ticket.tourists ?? []) {
					const tourist = t.tourist
					if (tourist?.deleted_at) {
						const touristName = tourist.name
						throw new Error(
							`Tourist "${touristName}" has been removed from the trip. Please remove this tourist from the activity "${activityName}" as well.`
						)
					}
				}
			}
		}
	}
}

function useVoucherLayoutBreakpoints() {
	const { xs, sm, md, lg, xl, vw } = useBreakpoints()
	const xxs = vw < 480
	const isSmallViewport = xxs || xs || sm
	const isNarrowViewport = xs || sm || md || lg
	const resGap: React.ComponentProps<typeof Grid>["gap"] | null =
		isNarrowViewport ? "4" : null
	return { isNarrowViewport, isSmallViewport, resGap, xxs, xs, sm, md, lg, xl }
}

function getActivityVoucherSchema({ totalPax }: { totalPax: number }) {
	return Validator.object({
		upload_method: Validator.mixed<TUploadMethods>().required(
			"Upload method is required"
		),

		remove_branding: Validator.boolean().optional().nullable(),

		file_url: Validator.string().url().optional(),

		files: Validator.array()
			.of(
				Validator.object({
					url: Validator.string().url().optional(),
					upload: Validator.mixed<File>().optional(),
				})
			)
			.when("upload_method", {
				is: (val: TUploadMethods) => val === "Attach File",
				then: (schema) =>
					schema
						.required("Please attach a file to continue.")
						.min(
							1,
							"At least one file needs to be attached before proceeding."
						),

				otherwise: (schema) => schema.optional(),
			}),
		notes: Validator.string().optional().nullable(),

		voucher_activities: Validator.array()
			.of(
				Validator.object({
					title: Validator.string().optional(),
					valid_till: Validator.date()
						.typeError("Invalid date")
						.optional()
						.nullable(),
					remarks: Validator.string().optional().nullable(),
					tickets: Validator.array()
						.of(
							Validator.object({
								ticket_number: Validator.string().required(
									"PNR/Ticket/Confirmation No. number is required"
								),
								no_of_pax: Validator.number()
									.required("No of pax is required")
									.min(1, "No of pax should be at least 1")
									.max(totalPax, `No of pax cannot be more than ${totalPax}`),

								generated_from_qr: Validator.boolean().optional().nullable(),

								remarks: Validator.string().optional().nullable(),

								tourists: Validator.array()
									.of(
										Validator.object({
											id: Validator.number().optional(),

											tourist: Validator.mixed<TTourist>().optional(),

											ticket_number: Validator.string().optional(),
										})
									)
									.optional()
									.test("match-no-of-pax", function (tourists) {
										const { parent } = this
										const noOfPax = parent?.no_of_pax
										if (!tourists || !Array.isArray(tourists)) return true
										if (typeof noOfPax !== "number") return true
										if (tourists.length > noOfPax) {
											return this.createError({
												message: `You have added ${tourists.length} tourist${tourists.length > 1 ? "s" : ""} but only ${noOfPax} member${noOfPax > 1 ? "s are" : " is"} allowed.`,
											})
										}
										return true
									}),
							})
						)
						.required()
						.min(1, "At least one ticket is required"),
				})
			)
			.optional(),
	})
}

export function GenerateVoucherForm({
	booking,
	totalPax,
	children,
	onSuccess,
	regenrate,
	voucherId,
}: {
	booking: TTravelActiviytBooking
	totalPax: number
	children: React.ReactNode
	onSuccess: (batch?: TBatch) => void
	regenrate?: boolean
	voucherId?: number
}) {
	const [isOpen, open, close] = useDialog()
	const {
		title,
		supplier,
		start_time_formatted,
		duration_formatted,
		end_time_formatted,
		ticket_tourist_configurations_full_name,
		trip_id,
	} = booking
	const xhr = useXHR()
	const { isNarrowViewport, resGap } = useVoucherLayoutBreakpoints()

	const activityVoucherSchema = getActivityVoucherSchema({ totalPax })

	return (
		<Fragment>
			<Button onClick={() => open()} title="Generate Voucher" inline>
				{children}
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => close()}
				xl
				title={
					<Box paddingLeft="4">
						<Text>Generate Voucher</Text>
					</Box>
				}
			>
				<RegenrateProcess
					isOpen={isOpen}
					regenrate={regenrate}
					voucherId={voucherId}
				>
					{({ initialValues }) => (
						<Form<TActivityVoucherForm>
							key={booking.id}
							validate={validateFormValues(activityVoucherSchema)}
							initialValues={
								regenrate
									? initialValues
									: {
											booking_id: booking.id,
											timezone_offset: config.timezoneOffset,
											upload_method: "Attach File",
										}
							}
							onSubmit={withServerErrors(async (values) => {
								let payload: FormData | Record<string, unknown>
								let headers: Record<string, string> = {}

								if (values.upload_method === "Attach File") {
									const formData = new FormData()
									const { booking_id, timezone_offset, files } = values

									formData.append("booking_id", String(booking_id))
									formData.append("timezone_offset", String(timezone_offset))

									if (Array.isArray(files) && files.length > 0) {
										files.forEach(({ upload, url }, i) => {
											if (upload instanceof File) {
												formData.append(`files[${i}][upload]`, upload)
											}
											if (typeof url === "string" && url.trim() !== "") {
												formData.append(`files[${i}][url]`, url)
											}
										})
									}
									payload = formData
									headers = { "Content-Type": "multipart/form-data" }
								} else if (values.upload_method === "Upload QR") {
									checkDeletedTourist(values)
									const voucher_activities = values?.voucher_activities?.map(
										(subActivity) => {
											const {
												__id: _activityId,
												tickets: activityTickets,
												valid_till,
												...activity
											} = subActivity
											const tickets = activityTickets.map((_ticket) => {
												const {
													__id: _ticketId,
													tourists: ticketTourists,
													generated_from_qr,
													...ticket
												} = _ticket as NonNullable<
													TActivityVoucherForm["voucher_activities"]
												>[number]["tickets"][number]
												const tourists = ticketTourists?.map((_tourist) => {
													const { __id: _touristId, ...tourist } =
														_tourist as NonNullable<
															NonNullable<
																TActivityVoucherForm["voucher_activities"]
															>[number]["tickets"][number]["tourists"]
														>[number]
													return {
														id: tourist?.tourist?.id,
														ticket_number: tourist.ticket_number,
													}
												})
												return {
													...ticket,
													generated_from_qr: generated_from_qr ? 1 : 0,
													tourists,
												}
											})
											return {
												...activity,
												valid_till: valid_till
													? formatDate(
															valid_till,
															"YYYY-MM-DD HH:mm"
														).toString()
													: undefined,
												tickets,
											}
										}
									)
									const {
										voucher_activities: _voucher_activities,
										id,
										files,
										sent_at,
										sent_by,
										status,
										...restValues
									} = values
									payload = {
										voucher_activities: voucher_activities,
										...restValues,
										show_prices: values.show_prices ? 1 : 0,
										remove_branding: values.remove_branding ? 1 : 0,
									}
								} else {
									throw new Error(
										`The selected upload method "${values.upload_method}" is not supported. Please choose a valid option.`
									)
								}
								try {
									const response: TBatch = await xhr
										.post("/travel-activity-booking-vouchers", payload, {
											headers,
										})
										.then((response) => response.data.data as TBatch)
									close()
									onSuccess(response)
								} catch (error) {
									const e = error as Error
									throw new Error(e.message)
								}
							})}
							subscription={{ submitting: true }}
							mutators={{ ...arrayMutators }}
						>
							{({ submitting, form, handleSubmit }) => (
								<form onSubmit={handleSubmit}>
									<Dialog.Body paddingX="2">
										<Stack gap="2" paddingX="4">
											<Grid
												noGutters={isNarrowViewport}
												{...(resGap ? { paddingX: "4", gap: resGap } : {})}
											>
												<Col xl={4}>
													<Stack gap="1">
														<Inline gap="3" alignItems="center">
															<Heading
																as="h3"
																fontSize="lg"
																fontWeight="semibold"
															>
																Activity Details
															</Heading>
														</Inline>
														<Box>
															<Text color="muted">
																Please verify the activity details that will be
																used in the voucher. Edit if required.
															</Text>
														</Box>
													</Stack>
												</Col>
												<Col xl={8}>
													<Inline gap="4" flexWrap="wrap">
														<Stack gap="1">
															<Text fontWeight="semibold">Activity Name</Text>
															<Text color="muted" fontSize="base">
																{title}
															</Text>
														</Stack>
														{supplier ? (
															<Inline gap="2">
																<Box borderLeftColor="muted" borderWidth="1" />
																<Stack gap="1">
																	<Text fontWeight="semibold" wordBreak="all">
																		Supplier Name
																	</Text>
																	<Text color="muted" fontSize="base">
																		{supplier?.name}
																	</Text>
																</Stack>
															</Inline>
														) : null}
														{start_time_formatted &&
														duration_formatted &&
														end_time_formatted ? (
															<Inline gap="2">
																<Box borderLeftColor="muted" borderWidth="1" />
																<Stack gap="1">
																	<Text fontWeight="semibold">Timing</Text>
																	<Inline gap="1" alignItems="center">
																		<Box flex="1" minWidth="0">
																			<Text>
																				{joinAttributes(
																					start_time_formatted,
																					duration_formatted
																						? `${duration_formatted}${end_time_formatted ? ` (Ends: ${end_time_formatted})` : ``}`
																						: null
																				)}
																			</Text>
																		</Box>
																	</Inline>
																</Stack>
															</Inline>
														) : null}
														{ticket_tourist_configurations_full_name ? (
															<Inline gap="2">
																<Box borderLeftColor="muted" borderWidth="1" />
																<Stack gap="1">
																	<Text fontSize="sm" fontWeight="semibold">
																		Pax
																	</Text>
																	<Text color="muted" fontSize="base">
																		{ticket_tourist_configurations_full_name}
																	</Text>
																</Stack>
															</Inline>
														) : null}
													</Inline>
												</Col>
											</Grid>

											<Divider sm />

											<GetFieldValue<
												TUploadMethods | undefined
											> name="upload_method">
												{({ value: upload_method }) => (
													<Stack gap="2">
														<Grid
															noGutters={isNarrowViewport}
															{...(resGap
																? { paddingX: "4", gap: resGap }
																: {})}
														>
															<Col xl={4} paddingX="4">
																<Stack gap="1">
																	<Inline gap="3" alignItems="center">
																		<Heading
																			as="h3"
																			fontSize="lg"
																			fontWeight="semibold"
																		>
																			Choose Upload Method
																		</Heading>
																	</Inline>
																	<Box>
																		<Text color="muted">
																			Select a method to upload the voucher
																			document.
																		</Text>
																	</Box>
																</Stack>
															</Col>
															<Col xl={8}>
																<Inline
																	rounded="md"
																	overflow="hidden"
																	borderWidth="1"
																	collapseBelow="sm"
																	style={{ width: "fit-content" }}
																>
																	<Stack
																		padding="4"
																		bgColor={
																			upload_method === "Attach File"
																				? "primary"
																				: "default"
																		}
																		as="label"
																		cursor="pointer"
																		roundedRight="md"
																	>
																		<RadioInputField
																			id="Attach Files"
																			name="upload_method"
																			label="Attach Files"
																			help={
																				<Stack gap="1">
																					<Text fontSize="sm">
																						Upload your voucher as a file (PDF,
																						image, or document).
																					</Text>
																				</Stack>
																			}
																			value="Attach File"
																			onChange={(
																				e: React.ChangeEvent<HTMLInputElement>
																			) => {
																				form.batch(() => {
																					form.change(
																						"upload_method",
																						"Attach File"
																					)
																					form.change("files", [])
																					form.change("voucher_activities", [])
																				})
																			}}
																		/>
																	</Stack>
																	<Stack
																		padding="4"
																		bgColor={
																			upload_method === "Upload QR"
																				? "primary"
																				: "default"
																		}
																		as="label"
																		cursor="pointer"
																		roundedLeft="md"
																	>
																		<RadioInputField
																			id="Upload QR"
																			name="upload_method"
																			label="Upload QR"
																			help={
																				<Stack gap="1">
																					<Text fontSize="sm">
																						Upload your voucher’s QR code for
																						quick and seamless validation.
																					</Text>
																				</Stack>
																			}
																			onChange={(
																				e: React.ChangeEvent<HTMLInputElement>
																			) => {
																				form.batch(() => {
																					form.change(
																						"upload_method",
																						"Upload QR"
																					)
																					form.change("voucher_activities", [
																						addKeyToFieldArrayItem({
																							title: title,
																							valid_till: undefined,
																							remarks: "",
																							tickets: [
																								addKeyToFieldArrayItem({
																									ticket_number: "",
																									no_of_pax: 1,
																									remarks: "",
																									generated_from_qr: 0 as 0 | 1,
																									tourists: [
																										addKeyToFieldArrayItem({
																											tourist: undefined,
																											id: undefined,
																											ticket_number: "",
																										}),
																									],
																								}),
																							],
																						}),
																					])
																					form.change("notes", undefined)
																					form.change("show_prices", 0)
																					form.change("remove_branding", 0)
																				})
																			}}
																			value="Upload QR"
																		/>
																	</Stack>
																</Inline>
															</Col>
														</Grid>

														<AttatchFileFlow />
														<UploadQRFlow
															tripId={trip_id}
															totalPax={totalPax}
														/>
													</Stack>
												)}
											</GetFieldValue>

											<Divider sm />

											<Grid>
												<Col xl={{ span: 8, offset: 4 }}>
													<Stack gap="4">
														<SubmissionError />
														<Inline gap="4">
															<Button type="submit" disabled={submitting}>
																{submitting
																	? "Generating..."
																	: "Generate Voucher"}
															</Button>
															<Button
																level="secondary"
																onClick={() => close()}
																disabled={submitting}
															>
																Close
															</Button>
														</Inline>
													</Stack>
												</Col>
											</Grid>
										</Stack>
									</Dialog.Body>
								</form>
							)}
						</Form>
					)}
				</RegenrateProcess>
			</Dialog>
		</Fragment>
	)
}

function RegenrateProcess({
	isOpen,
	regenrate,
	children,
	voucherId,
}: {
	isOpen: boolean
	regenrate?: boolean
	children: (props: { initialValues: TActivityVoucher }) => React.ReactNode
	voucherId?: number
}) {
	const xhr = useXHR()

	const { data: voucherData, error } = useSWR(
		isOpen && regenrate && voucherId
			? `/travel-activity-booking-vouchers/${voucherId}?isOpen=${isOpen}&regenrate=${regenrate}`
			: null,
		() =>
			xhr
				.get<{
					data: TRegenrateActivityVoucher
				}>(`travel-activity-booking-vouchers/${voucherId}`)
				.then((resp) => resp.data.data)
	)

	const initialValues = useMemo(() => {
		const isFilePresent =
			Array.isArray(voucherData?.files) && voucherData.files.length > 0
		const data: TActivityVoucher = {
			id: voucherData?.id,
			upload_method: isFilePresent ? "Attach File" : "Upload QR",
			booking_id: voucherData?.booking_id ?? undefined,
			files: voucherData?.files?.map((url) => ({
				upload: undefined,
				url: url,
			})),
			timezone_offset: config.timezoneOffset,
			notes: voucherData?.notes,
			show_prices: voucherData?.show_prices ? 1 : 0,
			remove_branding: voucherData?.remove_branding ? 1 : 0,
			voucher_activities: voucherData?.voucher_activities?.map((va) => {
				const tickets = va.tickets.map((t) => {
					const tourists = t.tourists?.map((tourist) =>
						addKeyToFieldArrayItem({
							id: tourist.id,
							tourist: tourist.id ? tourist : undefined,
							ticket_number: tourist.ticket_number,
						})
					)
					return addKeyToFieldArrayItem({ ...t, tourists })
				})
				const valid_till = va.valid_till
					? localOrUtcTimestampToLocalDate(
							String(va.valid_till),
							String(va.valid_till)
						)
					: undefined
				return addKeyToFieldArrayItem({
					...va,
					valid_till: valid_till,
					tickets,
				})
			}),
			status: voucherData?.status,
		}
		return data
	}, [voucherData])

	if (!voucherData && !error && voucherId && regenrate) {
		return (
			<Inline justifyContent="center" alignItems="center" padding="4">
				<Button inline disabled title="Generating Voucher">
					<Spinner />
				</Button>
			</Inline>
		)
	} else if (error) {
		const e = error as Error
		return (
			<Inline justifyContent="center" alignItems="center" padding="4">
				<Alert status="error">
					{e.message || "Failed to load voucher data."}
				</Alert>
			</Inline>
		)
	}

	return <>{children({ initialValues })}</>
}

function AttatchFileFlow() {
	const { value: upload_method } =
		useFieldValue<TUploadMethods>("upload_method")
	if (upload_method !== "Attach File") return null
	return (
		<>
			<Divider sm />
			<Grid>
				<Col xl={4}>
					<Stack gap="1">
						<Inline gap="3" alignItems="center">
							<Heading as="h3" fontSize="lg" fontWeight="semibold">
								Attach File
							</Heading>
						</Inline>
						<Box>
							<Text color="muted">
								Choose a file to upload from your device
							</Text>
						</Box>
					</Stack>
				</Col>
				<Col xl={8}>
					<Component initialState={1}>
						{({ state: fileKey, setState: setFileKey }) => (
							<Component initialState={false}>
								{({ state: showAddMore, setState: setShowAddMore }) => (
									<Box>
										<FieldArray<Required<TActivityVoucher>["files"][number]>
											name={`files`}
											key={"files"}
										>
											{({ fields: files }) => {
												const addFile = (selectedFiles: FileList | null) => {
													if (!selectedFiles || selectedFiles.length === 0)
														return
													// Abort the entire process if any file is greater than 5 MB
													const overSizedFile = Array.from(selectedFiles).find(
														(file) => file.size > 5 * 1024 * 1024
													)
													if (overSizedFile) {
														globalThis.alert(
															"File must be in JPG, JPEG, PNG, or PDF format and each file must be under 5 MB in size."
														)
														setFileKey(fileKey + 1)
														return
													}

													Array.from(selectedFiles)
														.map((file: File) => ({
															upload: file,
															url: undefined,
														}))
														.forEach((item) => files.push(item))
													setFileKey(fileKey + 1)
													setShowAddMore(false)
												}

												const removeFile = (index: number) => {
													files.remove(index)
												}
												return (
													<Stack gap="2">
														<Stack gap="4">
															{files.map((file, index) => (
																<GetFieldValue<
																	Required<TActivityVoucher>["files"][number]
																>
																	name={file}
																	key={file}
																>
																	{({ value: { upload: file, url } }) => {
																		if (file) {
																			return (
																				<>
																					<Inline
																						width="full"
																						gap="2"
																						alignItems="center"
																						key={
																							file.name +
																							file.type +
																							file.size +
																							index
																						}
																					>
																						<Inline
																							width="full"
																							justifyContent="between"
																							alignItems="center"
																							paddingX="4"
																							paddingY="1"
																							borderColor="muted"
																							borderWidth="1"
																							rounded="md"
																							key={index}
																							bgColor="subtle"
																						>
																							<Inline
																								alignItems="center"
																								gap="2"
																							>
																								{file.type.startsWith(
																									"image/"
																								) ? (
																									<Icons.Photograph size="5" />
																								) : (
																									<Icons.Document size="5" />
																								)}
																								<Stack>
																									<Text>{file.name}</Text>
																									<Text>
																										{formatFileSize(file.size)}
																									</Text>
																								</Stack>
																							</Inline>
																							<Inline gap="4">
																								<FilePreview file={file} />
																								<Button
																									onClick={() =>
																										removeFile(index)
																									}
																									inline
																								>
																									<Icons.Cancel color="danger" />
																								</Button>
																							</Inline>
																						</Inline>
																					</Inline>
																					<Divider sm marginY="0" />
																				</>
																			)
																		} else if (url) {
																			const isPDF = /\.pdf(\?|#|$)/i.test(url)
																			const name = url.split("/").at(-1)
																			return (
																				<>
																					<Inline
																						width="full"
																						gap="2"
																						alignItems="center"
																						key={url}
																					>
																						<Inline
																							width="full"
																							justifyContent="between"
																							alignItems="center"
																							paddingX="4"
																							paddingY="1"
																							borderColor="muted"
																							borderWidth="1"
																							rounded="md"
																							key={index}
																							bgColor="subtle"
																						>
																							<Inline
																								alignItems="center"
																								gap="2"
																							>
																								{isPDF ? (
																									<Icons.Document size="5" />
																								) : (
																									<Icons.Photograph size="5" />
																								)}
																								<Stack gap="1">
																									<Text>{name}</Text>
																									<Box>
																										<Badge success>
																											Already uploaded
																										</Badge>
																									</Box>
																								</Stack>
																							</Inline>
																							<Inline gap="4">
																								<FilePreview url={url} />
																								<Button
																									onClick={() =>
																										removeFile(index)
																									}
																									inline
																								>
																									<Icons.Cancel color="danger" />
																								</Button>
																							</Inline>
																						</Inline>
																					</Inline>
																					<Divider sm marginY="0" />
																				</>
																			)
																		}
																	}}
																</GetFieldValue>
															))}
														</Stack>
														{showAddMore || files.length === 0 ? (
															<FileInputField
																id="file-input"
																onChange={addFile}
																key={fileKey}
																label="Select File"
																name="file"
																multiple
																accept="image/jpg,image/jpeg,image/png,.pdf"
																help="File must be in JPG, JPEG, PNG, or PDF format and each file must be under 5 MB in size."
															/>
														) : (
															<Box>
																<Button
																	onClick={() => {
																		setShowAddMore(true)
																	}}
																>
																	<Icons.Plus /> Add More
																</Button>
															</Box>
														)}
													</Stack>
												)
											}}
										</FieldArray>
									</Box>
								)}
							</Component>
						)}
					</Component>
				</Col>
			</Grid>
		</>
	)
}

function FilePreview({ file, url }: { file?: File; url?: string }) {
	const [showPreview, setShowPreview] = useState(false)
	const [previewURL, setPreviewURL] = useState<string | undefined>(undefined)
	// Determine URL
	useEffect(() => {
		if (file) {
			const objectURL = URL.createObjectURL(file)
			setPreviewURL(objectURL)
			return () => URL.revokeObjectURL(objectURL)
		} else if (url) {
			setPreviewURL(url)
		}
	}, [file, url])

	if (!previewURL) return null

	const isPDF =
		file?.type === "application/pdf" || /\.pdf(\?|#|$)/i.test(previewURL)
	const name = file?.name || previewURL.split("/").at(-1)
	return (
		<>
			<Button
				onClick={() => {
					setShowPreview(true)
				}}
				inline
			>
				<Icons.ArrowExpand color="primary" />
			</Button>
			<Dialog
				open={showPreview}
				onClose={() => {
					setShowPreview(false)
				}}
				lg
				closeOnEscape
			>
				<Dialog.Body>
					<Box padding="4" rounded="md">
						{isPDF ? (
							<iframe
								title={name}
								src={previewURL}
								width="100%"
								height="600px"
								style={{ border: "none" }}
							/>
						) : (
							<img
								src={previewURL}
								alt={name}
								style={{
									width: "100%",
									maxHeight: "70vh",
									objectFit: "contain",
								}}
							/>
						)}
					</Box>
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={() => setShowPreview(false)} level="tertiary">
						Close
					</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function UploadQRFlow({
	tripId,
	totalPax,
}: {
	tripId: number
	totalPax: number
}) {
	const { isNarrowViewport, resGap } = useVoucherLayoutBreakpoints()
	const { value: upload_method } =
		useFieldValue<TUploadMethods>("upload_method")

	if (upload_method !== "Upload QR") return null
	return (
		<>
			<Divider sm />

			<Grid
				noGutters={isNarrowViewport}
				{...(resGap ? { paddingX: "4", gap: resGap } : {})}
			>
				<Col sm={12} xl={4}>
					<Stack gap="1">
						<Inline gap="3" alignItems="center">
							<Heading as="h3" fontSize="lg" fontWeight="semibold">
								Activity Included
							</Heading>
						</Inline>
						<Box>
							<Text color="muted">
								Add and manage the services covered within this booking.
							</Text>
						</Box>
					</Stack>
				</Col>
				<Col sm={12} xl={8}>
					<VoucherActivitiesFieldArray tripId={tripId} totalPax={totalPax} />
				</Col>
			</Grid>

			<Divider sm />

			<Grid
				noGutters={isNarrowViewport}
				{...(resGap ? { paddingX: "4", gap: resGap } : {})}
			>
				<Col xl={4}>
					<Stack gap="1">
						<Inline gap="3" alignItems="center">
							<Heading as="h3" fontSize="lg" fontWeight="semibold">
								Voucher Preferences
							</Heading>
						</Inline>
						<Box>
							<Text color="muted" fontSize="sm">
								Configure branding options for this voucher.
							</Text>
						</Box>
					</Stack>
				</Col>
				<Col xl={8}>
					<CheckboxInputField
						id="remove_branding"
						name="remove_branding"
						label="Remove Branding"
						help="Select the checkbox to remove branding from the generated PDF."
					/>
				</Col>
			</Grid>

			<Divider sm />

			<Grid
				noGutters={isNarrowViewport}
				{...(resGap ? { paddingX: "4", gap: resGap } : {})}
			>
				<Col xl={4}>
					<Stack gap="1">
						<Inline gap="3" alignItems="center">
							<Heading as="h3" fontSize="lg" fontWeight="semibold">
								Terms & Remarks
							</Heading>
						</Inline>
						<Box>
							<Text color="muted" fontSize="sm">
								Add important terms, conditions, or instructions for the
								voucher.
							</Text>
						</Box>
					</Stack>
				</Col>
				<Col xl={8}>
					<MarkdownInputField
						name="notes"
						label="Notes (optional)"
						max={2000}
						placeholder="enter notes content"
						rows={6}
						secondaryLabel="For Activity Voucher"
					/>
				</Col>
			</Grid>
		</>
	)
}

type TabMode = "scan" | "upload"
type TQRScanner = {
	method: TabMode
	qrFile?: File
}

interface QRScanResult {
	value: string
	imageUrl?: string
}

function PreviewResult({
	showRemove,
	result,
	onRetry,
	onRemove,
}: {
	showRemove?: boolean
	result: QRScanResult
	onRetry: () => void
	onRemove: () => void
}) {
	return (
		<Stack justifyContent="center" alignItems="center" gap="4" rounded="md">
			{result.imageUrl && (
				<Box
					as="img"
					src={result.imageUrl}
					alt="QR Result"
					rounded="md"
					style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "250px" }}
				/>
			)}
			<Inline width="full" justifyContent="center">
				<Text wordBreak="all">{result.value}</Text>
			</Inline>
			<Inline gap="4" width="full">
				<Button fullWidth status="primary" onClick={onRetry}>
					<Icons.Refresh /> Retry
				</Button>
				{showRemove && (
					<Button inline onClick={onRemove}>
						<Icons.Cancel /> Remove
					</Button>
				)}
			</Inline>
		</Stack>
	)
}

function QRScannerDialog({
	name,
	onSave,
}: {
	name: string
	onSave: (value: string) => void
}) {
	const qrReaderId = "qr-reader_" + useId()
	const qrUploadReaderId = "qr-upload-reader_" + useId()
	const [isOpen, open, close] = useDialog()
	const { value: ticket_number } = useFieldValue<string>(
		`${name}.ticket_number`
	)
	const { value: generated_from_qr } = useFieldValue<boolean>(
		`${name}.generated_from_qr`
	)
	const isQRPresentCheck = generated_from_qr && ticket_number

	const [result, setResult] = useState<QRScanResult | undefined>(undefined)
	const [isScanning, setIsScanning] = useState(false)
	const scannerRef = useRef<Html5QrcodeScanner | null>(null)

	const clearScanner = useCallback(() => {
		scannerRef.current?.clear()
		scannerRef.current = null
	}, [])

	const handleScanSuccess = useCallback(
		(value: string) => {
			const container = document.getElementById(qrReaderId)
			let imageUrl: string | undefined
			if (container) {
				const videoEL = container.querySelector("video")
				if (videoEL) {
					const canvas = document.createElement("canvas")
					canvas.width = videoEL?.videoWidth
					canvas.height = videoEL?.videoHeight
					canvas.getContext("2d")?.drawImage(videoEL, 0, 0)
					imageUrl = canvas.toDataURL("image/png")
				}
			}
			setResult({ value, imageUrl })
			setIsScanning(false)
			clearScanner()
		},
		[qrReaderId, clearScanner]
	)

	useEffect(() => {
		if (isScanning && !scannerRef.current) {
			const scanner = new Html5QrcodeScanner(
				qrReaderId,
				{
					qrbox: { width: 250, height: 250 },
					fps: 5,
					showZoomSliderIfSupported: true,
					rememberLastUsedCamera: true,
				},
				false
			)

			scanner.render(handleScanSuccess, console.warn)
			scannerRef.current = scanner
		}
		// Check camera permission and auto-start if granted
	}, [handleScanSuccess, isScanning, qrReaderId])

	const handleFileUpload = async (file: File) => {
		const scanner = new Html5Qrcode(qrUploadReaderId)
		try {
			const decodedText = await scanner.scanFile(file, true)
			setResult({ value: decodedText, imageUrl: URL.createObjectURL(file) })
		} catch {
			setResult({
				value: QR_ERRORS.DETECTION_FAILED,
				imageUrl: undefined,
			})
		}
		scanner.clear()
	}

	const resetScan = () => {
		setResult(undefined)
		setIsScanning(false)
	}

	return (
		<>
			<Button
				onClick={() => {
					open()
				}}
				status={isQRPresentCheck ? undefined : "primary"}
			>
				{isQRPresentCheck ? <Icons.ScanQRCode /> : <Icons.Upload />}{" "}
				{isQRPresentCheck ? "Update QR" : "Upload QR"}
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => {
					clearScanner()
					resetScan()
					close()
				}}
				sm
				closeOnEscape
			>
				<Dialog.Header>
					<Dialog.Title>Upload or Scan QR Code</Dialog.Title>
				</Dialog.Header>
				<Form<TQRScanner>
					initialValues={{
						method: undefined,
						qrFile: undefined,
					}}
					onSubmit={() => {
						if (result?.value === QR_ERRORS.DETECTION_FAILED) {
							throw new Error(QR_ERRORS.DETECTION_FAILED)
						}
						if (result?.value) {
							onSave(result.value)
							close()
							setResult(undefined)
						}
					}}
					subscription={{ submitting: true }}
				>
					{({ form, handleSubmit }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Inline gap="4" collapseBelow="md">
									<Box flex="1" padding="2" rounded="md" borderWidth="1">
										<RadioInputField
											name="method"
											label="Scan QR"
											value="scan"
											onChange={(e) => {
												resetScan()
												form.change("method", e.currentTarget.value as TabMode)
												setIsScanning(true)
											}}
											expandLabel
											help={
												<Text>
													Scan the QR code directly from the voucher using your
													device camera.
												</Text>
											}
										/>
									</Box>
									<Box flex="1" padding="2" rounded="md" borderWidth="1">
										<RadioInputField
											name="method"
											label="Upload QR"
											value="upload"
											onChange={(e) => {
												setIsScanning(false)
												setResult(undefined)
												clearScanner()
												form.change("method", e.currentTarget.value as TabMode)
												form.change("qrFile", undefined)
											}}
											expandLabel
											help={
												<Text>Upload a QR code image from your device.</Text>
											}
										/>
									</Box>
								</Inline>
								<Divider sm />
								<GetFieldValue<TabMode> name="method">
									{({ value: method }) =>
										method === "scan" ? (
											<ScanQR
												qrReaderId={qrReaderId}
												result={result}
												onRetry={() => {
													setResult(undefined)
													setIsScanning(true)
												}}
												onRemove={() => {
													resetScan()
												}}
											/>
										) : method === "upload" ? (
											<UploadQR
												qrUploadReaderId={qrUploadReaderId}
												result={result}
												onRetry={() => {
													setResult(undefined)
												}}
												onRemove={() => {
													setResult(undefined)
												}}
												handleFileUpload={handleFileUpload}
											/>
										) : null
									}
								</GetFieldValue>
							</Dialog.Body>
							<Dialog.Footer>
								<Button
									type="submit"
									disabled={
										!result || result.value === QR_ERRORS.DETECTION_FAILED
									}
								>
									Save
								</Button>
								<Button
									level="secondary"
									onClick={() => {
										clearScanner()
										resetScan()
										close()
									}}
								>
									Close
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function DownloadVoucher({
	batch,
	voucher,
	onSuccess,
	booking,
	totalPax,
}: {
	totalPax: number
	batch?: TBatch
	voucher?: TActivityVoucher
	onSuccess: (batch?: TBatch) => void
	booking: TTravelActiviytBooking
}) {
	return (
		<Batch
			voucher={voucher}
			batch={batch}
			onFinish={(batch) => {
				onSuccess(batch)
			}}
		>
			{() => {
				if (voucher?.status === "failed") {
					// 1️⃣ Voucher failed → regenerate
					return (
						<GenerateVoucherForm
							totalPax={totalPax}
							booking={booking}
							onSuccess={onSuccess}
							regenrate={true}
							voucherId={voucher?.id}
						>
							<Icons.Refresh title="File not generated" />
						</GenerateVoucherForm>
					)
				} else if (voucher?.file_url) {
					// 2️⃣ Voucher ready → download
					return (
						<Button<"a">
							as="a"
							inline
							href={voucher.file_url}
							title="Download Voucher"
							target="_blank"
							rel="noopener noreferrer"
						>
							<Icons.DocumentDownload />
						</Button>
					)
				} else {
					// 3️⃣ Voucher and batch are being processed -> show spinner
					return (
						<Button inline disabled title="Generating Voucher">
							<Spinner />
						</Button>
					)
				}
			}}
		</Batch>
	)
}

export type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

export function Batch({
	batch,
	children,
	onFinish,
	voucher,
}: {
	batch?: TBatch
	onFinish: (batch: TBatch) => void
	children: (props: { isCompleted: boolean }) => React.ReactNode
	voucher?: TActivityVoucher
}) {
	const xhr = useXHR()
	const isVoucherGenerationFailed = voucher?.status === "failed"
	const shouldPollBatch = Boolean(
		batch?.id && !voucher?.file_url && !isVoucherGenerationFailed
	)
	const { data: currentBatch } = useSWR(
		shouldPollBatch ? `/batch/${batch?.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batchData) => {
					if (batchData.finishedAt) {
						onFinish(batchData)
					}
					return batchData
				}),
		{
			refreshInterval: 1000,
		}
	)
	return children({ isCompleted: Boolean(currentBatch?.finishedAt) })
}

function ScanQR({
	qrReaderId,
	result,
	onRetry,
	onRemove,
}: {
	qrReaderId: string
	result?: QRScanResult
	onRetry: () => void
	onRemove: () => void
}) {
	return (
		<Box className="qr-scan-container">
			<Box id={qrReaderId} />
			{result && (
				<PreviewResult result={result} onRetry={onRetry} onRemove={onRemove} />
			)}
		</Box>
	)
}

function UploadQR({
	qrUploadReaderId,
	result,
	onRetry,
	onRemove,
	handleFileUpload,
}: {
	qrUploadReaderId: string
	result?: QRScanResult
	onRetry: () => void
	onRemove: () => void
	handleFileUpload: (file: File) => Promise<void>
}) {
	const fileInputId = "upload-qr"
	const form = useForm()
	const [fileKey, setFileKey] = useState(0)
	const helpText =
		"File must be JPG/JPEG/PNG/PDF file with less than 1 MB each in size."
	return (
		<Stack gap="2">
			<Box id={qrUploadReaderId} style={{ visibility: "hidden" }} />
			{result ? (
				<PreviewResult
					result={result}
					onRetry={() => {
						onRetry()
						setTimeout(() => {
							const element = document.getElementById(fileInputId)
							if (element) element.click()
						}, 0)
					}}
					onRemove={onRemove}
					showRemove
				/>
			) : (
				<FileInputField
					id={fileInputId}
					onChange={(selectedFiles) => {
						const file =
							selectedFiles && selectedFiles.length > 0
								? selectedFiles[0]
								: undefined
						if (!file) return
						if (file.size < 1 * 1024 * 1024) {
							form.change("qrFile", file)
							handleFileUpload(file)
						} else {
							globalThis.alert(helpText)
						}
						setFileKey(fileKey + 1)
					}}
					key={fileKey}
					label="Select File"
					name="qrFile"
					accept={`image/jpg,image/jpeg,image/png,.pdf`}
					help={helpText}
				/>
			)}
		</Stack>
	)
}

function VoucherActivityRow({
	name,
	index,
	removeAllowed,
	onRemove,
	tripId,
	totalPax,
	isSingleActivity,
}: {
	name: string
	index: number
	removeAllowed: boolean
	onRemove: (idx: number) => void
	tripId: number
	totalPax: number
	isSingleActivity: boolean
}) {
	const { value: remarks } = useFieldValue<
		Required<TActivityVoucher>["voucher_activities"][number]["remarks"]
	>(`${name}.remarks`)
	const { isNarrowViewport, resGap, xxs } = useVoucherLayoutBreakpoints()
	const [showRemarks, setShowRemarks] = useState(Boolean(remarks?.trim()))

	return (
		<Fragment>
			<Stack bgColor="subtle" rounded="md" gap="2">
				<Grid
					paddingY="2"
					noGutters={isNarrowViewport}
					{...(resGap ? { paddingX: "4", gap: resGap } : {})}
				>
					<Col sm={12} md={showRemarks ? 8 : 5} xl={showRemarks ? 8 : 5}>
						<TextInputField
							disabled={isSingleActivity}
							style={{ cursor: isSingleActivity ? "not-allowed" : "text" }}
							name={`${name}.title`}
							label="Activity Name"
							type="text"
							required
							placeholder="Name of Activity"
						/>
					</Col>
					<Col
						xl={showRemarks ? "auto" : 4}
						md="auto"
						lg="auto"
						sm="auto"
						xs={xxs ? 12 : 6}
					>
						<DatePickerField
							name={`${name}.valid_till`}
							dateFormat="DD MMM YY"
							timeFormat
							label="Voucher Validity"
						/>
					</Col>
					{!showRemarks && (
						<Col
							xl="auto"
							md="auto"
							sm="auto"
							xs="auto"
							paddingTop={xxs ? "0" : "6"}
						>
							<Button onClick={() => setShowRemarks(true)}>
								<Icons.Plus /> Add Remarks
							</Button>
						</Col>
					)}
				</Grid>
				{showRemarks && (
					<Grid
						noGutters={isNarrowViewport}
						{...(resGap ? { paddingX: "4", gap: resGap } : {})}
					>
						<Col sm={12} md={12} xl={12}>
							<TextAreaInputField
								name={`${name}.remarks`}
								label="Remarks"
								placeholder="Any remarks regarding activity"
							/>
						</Col>
					</Grid>
				)}
				<VoucherTicketsFieldArray
					name={name}
					tripId={tripId}
					totalPax={totalPax}
				/>
			</Stack>
			{removeAllowed && (
				<Inline justifyContent="end" alignItems="center">
					<Button inline onClick={() => onRemove(index)}>
						<Icons.Cancel /> Remove
					</Button>
				</Inline>
			)}
			<Divider sm />
		</Fragment>
	)
}

function VoucherActivitiesFieldArray({
	tripId,
	totalPax,
}: {
	tripId: number
	totalPax: number
}) {
	const form = useForm()
	return (
		<FieldArray<Required<TActivityVoucher>["voucher_activities"][number]>
			name="voucher_activities"
			key="voucher_activities"
		>
			{({ fields: voucher_activities }) => (
				<Stack gap="4">
					{voucher_activities.map((name, index) => (
						<VoucherActivityRow
							key={getFieldArrayItemKey(form as never, name)}
							name={name}
							index={index}
							isSingleActivity={voucher_activities.length === 1}
							removeAllowed={(voucher_activities.length ?? 0) > 1}
							onRemove={() => voucher_activities.remove(index)}
							tripId={tripId}
							totalPax={totalPax}
						/>
					))}
					<Box>
						<Button
							fullWidth={false}
							onClick={() => {
								voucher_activities.push(
									addKeyToFieldArrayItem({
										title: "",
										valid_till: undefined,
										remarks: "",
										tickets: [
											addKeyToFieldArrayItem({
												ticket_number: "",
												generated_from_qr: 0 as 0 | 1,
												no_of_pax: 1,
												remarks: "",
												tourists: [
													addKeyToFieldArrayItem({
														id: undefined,
														tourist: undefined,
														ticket_number: "",
													}),
												],
											}),
										],
									})
								)
							}}
						>
							<Icons.Plus /> Add More
						</Button>
					</Box>
				</Stack>
			)}
		</FieldArray>
	)
}

function VoucherTicketsFieldArray({
	name,
	tripId,
	totalPax,
}: {
	name: string
	tripId: number
	totalPax: number
}) {
	const { isNarrowViewport, resGap, isSmallViewport, xxs, xs } =
		useVoucherLayoutBreakpoints()
	const form = useForm()
	return (
		<FieldArray<
			Required<TActivityVoucher>["voucher_activities"][number]["tickets"][number]
		>
			name={`${name}.tickets`}
			key={name}
		>
			{({ fields: tickets }) => (
				<Stack gap="4" paddingY="4" paddingX="2">
					{tickets.map((ticket, index) => {
						const isSingleTicket = tickets.length === 1
						return (
							<Stack
								key={getFieldArrayItemKey(form as never, ticket)}
								bgColor="default"
								rounded="md"
								borderColor="muted"
								borderWidth="1"
								paddingY="4"
								gap="4"
							>
								<Grid
									noGutters={isNarrowViewport}
									{...(resGap ? { paddingX: "4", gap: resGap } : {})}
								>
									<Col xl={4} md={4} sm={6}>
										<TextInputField
											name={`${ticket}.ticket_number`}
											label="PNR/Ticket/Confirmation No."
											type="text"
											placeholder="e.g. ACT-2026-00451"
											width="2/3"
										/>
									</Col>
									<Col xl={3} md={3} sm={5}>
										<TextInputField
											name={`${ticket}.no_of_pax`}
											label="No. of Members"
											type="number"
											required
											width="2/3"
											min={1}
											max={totalPax}
										/>
									</Col>
									<Col
										xl="auto"
										md="auto"
										sm="auto"
										xs="auto"
										paddingTop={isSmallViewport ? "0" : "6"}
									>
										<QRScannerDialog
											name={ticket}
											onSave={(value) => {
												if (value) {
													form.change(`${ticket}.ticket_number`, value)
													form.change(`${ticket}.generated_from_qr`, true)
												}
											}}
										/>
									</Col>
									{isSingleTicket ? null : (
										<Col
											paddingTop={isSmallViewport ? "0" : "6"}
											xl="auto"
											md="auto"
											sm="auto"
											xs="auto"
										>
											<Button
												onClick={() => {
													tickets.remove(index)
												}}
											>
												<Icons.Cancel />
											</Button>
										</Col>
									)}
									{tickets.length === index + 1 ? (
										<Col
											xl="auto"
											md="auto"
											sm="auto"
											xs="auto"
											paddingTop={isSmallViewport ? "0" : "6"}
										>
											<Button
												onClick={() => {
													tickets.push(
														addKeyToFieldArrayItem({
															ticket_number: "",
															no_of_pax: 1,
															generated_from_qr: 0 as 0 | 1,
															remarks: "",
															tourists: [
																addKeyToFieldArrayItem({
																	tourist: undefined,
																	id: undefined,
																	ticket_number: "",
																}),
															],
														})
													)
												}}
											>
												<Icons.Plus />
											</Button>
										</Col>
									) : null}
								</Grid>
								<Grid>
									<Col xl={12} md={12}>
										<TextAreaInputField
											name={`${ticket}.remarks`}
											label="Remarks"
										/>
									</Col>
								</Grid>
								<Box paddingX="4">
									<Divider sm width="2/3" />
								</Box>
								<Stack gap="4" paddingX={xxs || xs ? "1" : "4"}>
									<TouristsFieldArray name={ticket} tripId={tripId} />
								</Stack>
							</Stack>
						)
					})}
				</Stack>
			)}
		</FieldArray>
	)
}

function TouristsFieldArray({
	name,
	tripId,
}: {
	name: string
	tripId: number
}) {
	const { isNarrowViewport, resGap, xxs, xs, isSmallViewport } =
		useVoucherLayoutBreakpoints()
	const form = useForm()
	const { value: no_of_pax } = useFieldValue<number>(`${name}.no_of_pax`)
	return (
		<FieldArray<
			Required<
				Required<TActivityVoucher>["voucher_activities"][number]["tickets"][number]
			>["tourists"][number]
		>
			name={`${name}.tourists`}
		>
			{({ fields: tourists }) => {
				const isSingleTicket = tourists.length === 1
				if (tourists.length === 0) {
					return (
						<Box paddingX="4">
							<Button
								onClick={() => {
									tourists.push(
										addKeyToFieldArrayItem({
											tourist: undefined,
											id: undefined,
											ticket_number: "",
										})
									)
								}}
							>
								<Icons.Plus /> Add Tourist
							</Button>
						</Box>
					)
				} else {
					return tourists.map((tourist, index) => (
						<Inline
							key={getFieldArrayItemKey(form as never, tourist)}
							gap={xxs || xs ? "1" : "4"}
						>
							<Inline
								justifyContent={isSmallViewport ? "start" : "between"}
								alignItems={isSmallViewport ? "start" : "center"}
								paddingTop="6"
							>
								<Badge>{index + 1}</Badge>
							</Inline>
							<Grid
								noGutters={isNarrowViewport}
								{...(resGap ? { paddingX: "4", gap: resGap } : {})}
							>
								<Col xl={4} md={4}>
									<SelectField
										name={`${tourist}.tourist`}
										label="Tourist Name"
										select={SelectTourist}
										placeholder="Type to search..."
										tripId={tripId}
									/>
								</Col>
								<Col xl={4} md={5} sm={8} xs={12}>
									<TextInputField
										name={`${tourist}.ticket_number`}
										label="Ticket/Voucher No."
										type="string"
										placeholder="e.g. ACT-2026-00451"
									/>
								</Col>
								{isSingleTicket ? null : (
									<Col
										paddingTop={xs ? "0" : "6"}
										xl="auto"
										md="auto"
										sm="auto"
										xs="auto"
									>
										<Button
											onClick={() => {
												tourists.remove(index)
											}}
										>
											<Icons.Cancel />
										</Button>
									</Col>
								)}
								{tourists.length === index + 1 &&
								tourists.length < no_of_pax ? (
									<Col
										paddingTop={xs ? "0" : "6"}
										xl="auto"
										md="auto"
										sm="auto"
										xs="auto"
									>
										<Button
											onClick={() => {
												tourists.push(
													addKeyToFieldArrayItem({
														tourist: undefined,
														id: undefined,
														ticket_number: "",
													})
												)
											}}
										>
											<Icons.Plus />
										</Button>
									</Col>
								) : null}
							</Grid>
						</Inline>
					))
				}
			}}
		</FieldArray>
	)
}
